import React, { FC, useCallback } from 'react';

import { useQuery } from '@apollo/client';
import { Container, styled } from '@mui/material';
import { InstantSearch, InstantSearchProps } from 'react-instantsearch';
import { useParams, useNavigate } from 'react-router-dom';

import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import Hero from '~/components/Hero/Hero';
import { CenteredContainer } from '~/components/Hero/style';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import Search from '~/components/Search/Search';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import { DEFAULT_DISCOVER_URL, FILTERS, RM_PAGES } from '~/const';
import { useDiscoverContext } from '~/context/DiscoverContext';
import { useRmMetadata } from '~/context/RmContext';
import useScriptLoader, { SCRIPTS } from '~/context/ScriptLoader/useScriptLoader';
import { GET_SKILL_BY_SLUG } from '~/graphql/discover';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { CategoryInfo } from '~/pages/DiscoverPage/components/CategoryInfo/CategoryInfo';
import SERPContainer from '~/pages/DiscoverPage/components/SERPContainer/SERPContainer';
import Tabs from '~/pages/DiscoverPage/components/Tabs/Tabs';
import { buildSkillRoute, DISCOVER_SEARCH_ROUTE } from '~/routes';
import { css, CssUtilityType } from '~/styled';
import {
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SMALL_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { Maybe, Query, QueryGetSkillBySlugArgs, Skill, Tenant } from '~/types';
import { generateSeoTitle } from '~/utils/generateSeoTitle';
import { translate } from '~/utils/translate';
import getCanonicalUrl from '~/utils/urlUtils/getCanonicalUrl';

const getNavigationArray = (skillInfo?: Maybe<Skill>) => {
  let navigationArr = [
    {
      title: translate('page.discover.discover'),
      url: DEFAULT_DISCOVER_URL,
    },
  ];

  if (skillInfo) {
    navigationArr.push({
      title: skillInfo.title,
      url: buildSkillRoute(skillInfo.slug),
    });
  }

  return navigationArr;
};

interface SkillPageProps {
  tenant?: Maybe<Tenant>;
  algoliaIndexName: string;
  searchClient: InstantSearchProps['searchClient'];
  loadingKey: boolean;
  userLoading: boolean;
}

export const SkillPage: FC<SkillPageProps> = ({
  tenant,
  algoliaIndexName,
  searchClient,
  loadingKey,
  userLoading,
}) => {
  useScriptLoader(SCRIPTS.TEAMS_SHARE);
  const { t } = useTenantTranslation();
  const { setSearchState } = useDiscoverContext();
  const params = useParams();
  const { skill } = params;
  const navigate = useNavigate();

  useRmMetadata({ page: RM_PAGES.SkillPage });

  const attributesExcludedFromClear = [FILTERS.Skills];

  const { data: skillData, loading: skillLoading } = useQuery<
    Pick<Query, 'getSkillBySlug'>,
    QueryGetSkillBySlugArgs
  >(GET_SKILL_BY_SLUG, {
    variables: {
      slug: skill || '',
    },
    skip: !skill,
  });

  const skillInfo = skillData?.getSkillBySlug;
  const hasImage = !!skillInfo?.image_url;

  const handleSearch = (val: string) => {
    setSearchState((prevState) => ({
      ...prevState,
      query: val,
    }));
    navigate(`${DISCOVER_SEARCH_ROUTE}?s=${val}`);
  };

  const navigationArr = getNavigationArray(skillInfo);

  const canonicalUrl = getCanonicalUrl(buildSkillRoute(skillInfo?.slug), skillInfo?.tenants);

  const onChange = useCallback(({ uiState, setUiState }) => {
    setUiState(uiState);
  }, []);

  if (!skillInfo && !skillLoading) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  return (
    <>
      {skillInfo && (
        <SeoBlock
          data={{
            seo_title: generateSeoTitle(skillInfo?.title, tenant?.title),
            seo_description: skillInfo?.description || '',
            share_image: skillInfo?.image_url,
          }}
          canonicalUrl={canonicalUrl}
        />
      )}
      <InstantSearch
        searchClient={searchClient}
        indexName={algoliaIndexName}
        onStateChange={onChange}
      >
        <Hero
          title={t('page.discover.discover')}
          imageUrl={skillInfo?.image_url}
          wrapperCss={hasImage ? headerWrapperCss : undefined}
          contentWrapperCss={hasImage ? headerContentWrapperCss : undefined}
        >
          <CenteredContainer>
            <Search
              refine={handleSearch}
              withClear
              searchPlaceholder={t('page.discover.searchAllSkills')}
            />
            <Tabs />
          </CenteredContainer>
          {skillLoading && (
            <WithImgContainer>
              <LoadingBreadcrumbs xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
              <LoadingTitle xs={SKELETON_TEXT_HEIGHT_PX} />
              <LoadingDescription
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                lines={4}
                spacer={TINY_MARGIN_PX}
              />
            </WithImgContainer>
          )}
          {!skillLoading && hasImage && (
            <WithImgContainer>
              <Breadcrumbs crumbs={navigationArr} data-testid="skillBreadcrumbs" />
              <CategoryInfo title={skillInfo?.title} description={skillInfo?.description} />
            </WithImgContainer>
          )}
        </Hero>
        {!skillLoading && !hasImage && (
          <StyledContainer
            optionalStyles={
              hasImage ? generatedChildCategoriesContainer : generatedWithoutImageContainer
            }
          >
            {!hasImage && (
              <>
                <Breadcrumbs crumbs={navigationArr} data-testid="skillBreadcrumbs" />
                <CategoryInfo title={skillInfo?.title} description={skillInfo?.description} />
              </>
            )}
          </StyledContainer>
        )}
        <Container>
          <SERPContainer
            linkedInId={tenant?.linkedin_id}
            attributesExcludedFromClear={attributesExcludedFromClear}
            loadingApiKey={loadingKey || skillLoading}
            userLoading={userLoading}
            initialSkillId={skillInfo?.id || ''}
          />
        </Container>
      </InstantSearch>
    </>
  );
};

export default SkillPage;

const LoadingBreadcrumbs = styled(AnimatedSkeleton)`
  margin-bottom: ${SMALL_MARGIN_PX};
  width: 250px;
  max-width: 100%;
`;
const LoadingTitle = styled(AnimatedSkeleton)`
  width: 300px;
`;
const LoadingDescription = styled(AnimatedSkeletonMultiline)`
  margin-top: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: ${MIDDLE_MARGIN_PX};
  }
`;
const headerWrapperCss = ({ theme }) => css`
  margin-bottom: ${MIDDLE_MARGIN_PX};

  ${theme.breakpoints.up('md')} {
    padding-bottom: 0;
    margin-bottom: ${LARGE_MARGIN_PX};
  }
`;
const headerContentWrapperCss = css`
  padding-bottom: 0;
`;
const WithImgContainer = styled(Container)`
  padding-top: ${MIDDLE_MARGIN_PX};
  padding-bottom: ${MIDDLE_MARGIN_PX};

  &:after {
    position: absolute;
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${({ theme }) => theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-top: ${LARGE_MARGIN_PX};
    padding-bottom: ${MIDDLE_MARGIN_PX};
    position: relative;

    &:after {
      position: absolute;
      display: block;
      content: '';
      height: 3px;
      width: calc(100% - 32px);
      background-color: ${({ theme }) => theme.palette.common.violet};
      opacity: 0.3;
      left: 16px;
      bottom: 0;
    }
  }
`;

const generatedWithoutImageContainer = ({ theme }) => css`
  position: relative;
  margin-bottom: ${MIDDLE_MARGIN_PX};
  padding-bottom: ${MIDDLE_MARGIN_PX};

  &:after {
    position: absolute;
    display: block;
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${theme.breakpoints.up('md')} {
    margin-bottom: ${LARGE_MARGIN_PX};
  }
`;
const generatedChildCategoriesContainer = ({ theme }) => css`
  position: relative;
  margin-bottom: ${MIDDLE_MARGIN_PX};

  &:after {
    position: absolute;
    display: block;
    content: '';
    height: 3px;
    width: calc(100% - 32px);
    background-color: ${theme.palette.common.violet};
    opacity: 0.3;
    left: 16px;
    bottom: 0;
  }

  ${theme.breakpoints.up('md')} {
    display: none;
  }
`;
const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles: CssUtilityType }>`
  ${({ optionalStyles }) => optionalStyles}
`;
